import { useEffect, useRef } from "react";

/**
 * Ambient, decorative dot/connector network rendered on a single canvas.
 * - Small teal/gold dots drift slowly and continuously.
 * - Faint lines connect nearby dots (constellation effect), fading with distance.
 * - When `interactive` is on (homepage only), the mouse pointer traces a
 *   "compliance workflow": as it passes near dots they lock in one-by-one and
 *   light up through a traffic-light scale
 *   (non-compliant → high-risk → caution → low-risk → compliant). Bright lines
 *   connect the activated dots in order. Once every dot is lit, the trail fades
 *   and the sequence resets so it can start again.
 * - Respects prefers-reduced-motion: renders one static frame, no animation and
 *   no pointer tracking. Pointer tracking is also skipped on non-hover devices.
 * Purely decorative — aria-hidden and pointer-events-none.
 */

type Dot = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  color: string;
};

const TEAL = "0, 168, 150"; // #00A896
const GOLD = "253, 200, 90"; // #FDC85A

// Traffic-light workflow stages: non-compliant → compliant.
const STAGES = [
  "239, 68, 68", //  red    — non-compliant
  "249, 115, 22", // orange — high-risk
  "245, 166, 35", // amber  — caution
  "132, 204, 120", // green-light — low-risk
  "16, 185, 129", // green  — compliant
];

const STAGE_LABELS = [
  "Non-compliant",
  "High-risk",
  "Caution",
  "Low-risk",
  "Compliant",
];

type Props = {
  /** Enables mouse-driven traffic-light workflow (homepage only). */
  interactive?: boolean;
};

const AmbientDotNetwork = ({ interactive = false }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const canHover = window.matchMedia("(hover: hover)").matches;
    const isInteractive = interactive && !reduceMotion && canHover;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let dots: Dot[] = [];
    let rafId = 0;

    // Interaction state (refs-style locals; no React re-render per frame).
    const mouse = { x: -9999, y: -9999 };
    let activated: number[] = []; // ordered dot indices
    const activatedSet = new Set<number>();
    let resetAt = 0; // timestamp when a full trail should clear

    const buildDots = () => {
      // Density scaled to viewport area, capped for performance.
      const base = (width * height) / 26000;
      const count = Math.max(18, Math.min(60, Math.round(base)));
      dots = Array.from({ length: count }, (_, i) => {
        const isGold = i % 5 === 0; // ~4:1 teal to gold
        const speed = (0.12 + Math.random() * 0.22) * 0.6;
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.4; // upward-ish drift
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          r: 1 + Math.random() * 2,
          color: isGold ? GOLD : TEAL,
        };
      });
      activated = [];
      activatedSet.clear();
      resetAt = 0;
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildDots();
    };

    const connectDist = 130;
    const activateDist = 60; // pointer proximity to lock a dot in

    // Stage colour for the nth activated dot (one stage per step).
    const stageColor = (seqIndex: number) =>
      STAGES[Math.min(STAGES.length - 1, seqIndex)];

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Ambient proximity connector lines
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const a = dots[i];
          const b = dots[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < connectDist) {
            const alpha = (1 - dist / connectDist) * 0.28;
            ctx.strokeStyle = `rgba(${TEAL}, ${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Workflow trail: bright lines between consecutive activated dots
      if (isInteractive && activated.length > 1) {
        for (let k = 1; k < activated.length; k++) {
          const a = dots[activated[k - 1]];
          const b = dots[activated[k]];
          if (!a || !b) continue;
          ctx.strokeStyle = `rgba(${stageColor(k)}, 0.65)`;
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // Dots (activated ones glow in their stage colour)
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        const seq = isInteractive ? activated.indexOf(i) : -1;
        if (seq >= 0) {
          const color = stageColor(seq);
          ctx.shadowColor = `rgba(${color}, 0.9)`;
          ctx.shadowBlur = 10;
          ctx.fillStyle = `rgba(${color}, 0.95)`;
          ctx.beginPath();
          ctx.arc(d.x, d.y, d.r + 1.4, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;

          // Stage label beside the activated dot
          const label = STAGE_LABELS[Math.min(STAGE_LABELS.length - 1, seq)];
          ctx.font =
            "600 14px Calibri, 'Trebuchet MS', system-ui, sans-serif";
          ctx.textBaseline = "bottom";
          ctx.fillStyle = `rgba(${color}, 0.95)`;
          ctx.fillText(label, d.x + 10, d.y - 8);
        } else {
          ctx.fillStyle = `rgba(${d.color}, 0.6)`;
          ctx.beginPath();
          ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const step = () => {
      for (const d of dots) {
        d.x += d.vx;
        d.y += d.vy;
        // Wrap around edges with a small margin
        if (d.x < -10) d.x = width + 10;
        if (d.x > width + 10) d.x = -10;
        if (d.y < -10) d.y = height + 10;
        if (d.y > height + 10) d.y = -10;
      }

      if (isInteractive) {
        // Clear a completed trail after its brief pause.
        if (resetAt && performance.now() >= resetAt) {
          activated = [];
          activatedSet.clear();
          resetAt = 0;
        }

        // Lock in the nearest un-activated dot under the pointer.
        if (!resetAt && activated.length < STAGES.length && mouse.x > -9998) {
          let nearest = -1;
          let nearestDist = activateDist;
          for (let i = 0; i < dots.length; i++) {
            if (activatedSet.has(i)) continue;
            const dist = Math.hypot(dots[i].x - mouse.x, dots[i].y - mouse.y);
            if (dist < nearestDist) {
              nearestDist = dist;
              nearest = i;
            }
          }
          if (nearest >= 0) {
            activated.push(nearest);
            activatedSet.add(nearest);
            // Reached "Compliant" — hold the completed trail, then reset.
            if (activated.length >= STAGES.length) {
              resetAt = performance.now() + 1600;
            }
          }
        }
      }

      draw();
      rafId = requestAnimationFrame(step);
    };

    const onMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onMouseLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    const start = () => {
      cancelAnimationFrame(rafId);
      if (reduceMotion) {
        draw();
      } else {
        rafId = requestAnimationFrame(step);
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(rafId);
      } else if (!reduceMotion) {
        rafId = requestAnimationFrame(step);
      }
    };

    resize();
    start();

    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    if (isInteractive) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseout", onMouseLeave);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseout", onMouseLeave);
    };
  }, [interactive]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 -z-10 pointer-events-none"
    />
  );
};

export default AmbientDotNetwork;
